import {
  Announced,
  classNamesFunction,
  getTheme,
  IButtonProps,
  IconButton,
  ITeachingBubbleProps,
  ITeachingBubbleStyleProps,
  ITeachingBubbleStyles,
  Target,
  TeachingBubble,
} from 'office-ui-fabric-react';
import { useId } from '@uifabric/react-hooks';
import { getStyles } from 'office-ui-fabric-react/lib/components/TeachingBubble/TeachingBubble.styles';
import React, { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';

import { subhead2BoldFont } from '../Typography/Typography';

export interface IMultiStepTeachingBubbleProps {
  /**
   * titles of the steps
   */
  titles: string[];

  details: ReactNode[];

  initialPage?: number;

  /**
   * Tab Index
   **/
  tabIndex?: number;

  targets: Target[];

  onDismiss: ITeachingBubbleProps['onDismiss'];

  className?: string;
  didShowPage?: (page: number) => void;
}

const DialogHeader = styled.h2`
  ${subhead2BoldFont}
  margin: 0 0 14px 0;
`;

const theme = getTheme();
const styles = getStyles({ theme });
const classNames = classNamesFunction<ITeachingBubbleStyleProps, ITeachingBubbleStyles>()(styles, { theme: theme });

const UnstyledMultiStepTeachingBubble: FunctionComponent<IMultiStepTeachingBubbleProps> = ({
  titles,
  details,
  didShowPage,
  targets,
  onDismiss,
  className,
  initialPage = 0,
}) => {
  const [page, _setPage] = React.useState(initialPage);
  const titleId = useId('title');
  const contentId = useId('contentId');

  const setPage = React.useCallback(
    nextPage => {
      if (didShowPage) {
        didShowPage(nextPage);
      }

      _setPage(nextPage);
    },
    [didShowPage]
  );

  const incrementPage = React.useCallback(() => setPage((page + 1) % details.length), [details.length, page, setPage]);

  const decrementPage = React.useCallback(() => setPage((page - 1 + details.length) % details.length), [details.length, page, setPage]);

  const primaryButtonProps: IButtonProps = React.useMemo(
    () => ({
      children: 'Next',
      ariaLabel: 'next page',
      onClick: incrementPage,
    }),
    [incrementPage]
  );

  const secondaryButtonProps: IButtonProps = React.useMemo(
    () => ({
      children: 'Previous',
      ariaLabel: 'previous page',
      onClick: decrementPage,
    }),
    [decrementPage]
  );

  return (
    <TeachingBubble
      hasCloseButton={false}
      target={targets[page]}
      onDismiss={onDismiss}
      calloutProps={{
        role: 'alertdialog',
      }}
      primaryButtonProps={primaryButtonProps}
      secondaryButtonProps={secondaryButtonProps}
      footerContent={`${page + 1} of ${details.length}`}
      ariaLabelledBy={titleId}
      ariaDescribedBy={contentId}
    >
      {/* Set close button here instead of using hasCloseButton={true} to set
      title attribute - required for screen readers */}
      <IconButton
        className={classNames.closeButton}
        iconProps={{ iconName: 'Cancel' }}
        title={`Close ${titles[page]}`}
        onClick={onDismiss}
      />
      <div className={className}>
        <Announced message={`${titles[page]}. ${details[page]}`} />
        <DialogHeader id={titleId}>{titles[page]}</DialogHeader>
        <p aria-hidden="true">{details[page]}</p>
      </div>
    </TeachingBubble>
  );
};

const MultiStepTeachingBubble = styled(UnstyledMultiStepTeachingBubble)`
  min-height: 67px;
` as FunctionComponent<IMultiStepTeachingBubbleProps>;

export { MultiStepTeachingBubble };
